import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { Skeleton, SkeletonCard, SkeletonCircle, SkeletonStack } from '@/components/skeletons/Skeleton';
import { routePaths } from '@/routes/paths';
import { cn } from '@/utils/cn';

function PageFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100', className)}>
      <div className="app-page-content pb-6 pt-3">{children}</div>
    </div>
  );
}

function AuthLoadingSkeleton() {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-6 dark:bg-slate-950">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(24,142,196,0.12),transparent_28%)]" />
      <SkeletonCard className="relative w-full max-w-md px-6 py-7">
        <SkeletonStack className="space-y-4">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-10 w-32 rounded-full" />
        </SkeletonStack>
      </SkeletonCard>
    </div>
  );
}

function HeaderSkeleton({ titleWidth = 'w-40', subtitleWidth = 'w-64' }: { titleWidth?: string; subtitleWidth?: string }) {
  return (
    <SkeletonCard className="px-4 py-4 sm:px-5">
      <div className="flex items-center gap-3">
        <SkeletonCircle className="h-10 w-10 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className={cn('h-4', titleWidth)} />
          <Skeleton className={cn('h-3', subtitleWidth)} />
        </div>
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>
    </SkeletonCard>
  );
}

function FilterRowSkeleton({ chips = 4 }: { chips?: number }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: chips }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-24 rounded-full" />
      ))}
    </div>
  );
}

function ListPageSkeleton({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton />
        <SkeletonCard className="px-4 py-4 sm:px-5">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-44" />
              </div>
              <Skeleton className="h-10 w-28 rounded-full" />
            </div>
            <FilterRowSkeleton />
          </div>
        </SkeletonCard>

        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonCard key={index} className="px-4 py-4 sm:px-5">
              <div className="flex items-start gap-3">
                <SkeletonCircle className="h-11 w-11 shrink-0" />
                <div className="min-w-0 flex-1 space-y-3">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
                <Skeleton className="h-8 w-20 rounded-full" />
              </div>
            </SkeletonCard>
          ))}
        </div>

        <div className="px-1 pt-2">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">{title}</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
    </PageFrame>
  );
}

function DetailPageSkeleton({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-52" subtitleWidth="w-72" />
        <SkeletonCard className="px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <SkeletonCircle className="h-20 w-20 shrink-0" />
            <div className="min-w-0 flex-1 space-y-3">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-3 w-40" />
              <div className="flex flex-wrap gap-2 pt-1">
                <Skeleton className="h-8 w-24 rounded-full" />
                <Skeleton className="h-8 w-28 rounded-full" />
                <Skeleton className="h-8 w-20 rounded-full" />
              </div>
            </div>
          </div>
        </SkeletonCard>

        <div className="grid gap-3 lg:grid-cols-[1.6fr_1fr]">
          <SkeletonCard className="space-y-3 px-4 py-4 sm:px-5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-72" />
            <Skeleton className="h-28 w-full rounded-[1.1rem]" />
            <Skeleton className="h-28 w-full rounded-[1.1rem]" />
          </SkeletonCard>
          <SkeletonCard className="space-y-3 px-4 py-4 sm:px-5">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-48" />
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-[1rem]" />
            ))}
          </SkeletonCard>
        </div>

        <div className="px-1 pt-2">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">{title}</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
    </PageFrame>
  );
}

function FormPageSkeleton({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-48" subtitleWidth="w-72" />
        <div className="grid gap-3 lg:grid-cols-[1.4fr_0.9fr]">
          <SkeletonCard className="space-y-4 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-12 w-full rounded-2xl" />
            <Skeleton className="h-12 w-full rounded-2xl" />
            <Skeleton className="h-12 w-2/3 rounded-2xl" />
            <Skeleton className="h-28 w-full rounded-[1.1rem]" />
          </SkeletonCard>
          <SkeletonCard className="space-y-4 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-52" />
            <Skeleton className="h-24 w-full rounded-[1rem]" />
            <Skeleton className="h-24 w-full rounded-[1rem]" />
            <Skeleton className="h-12 w-full rounded-2xl" />
          </SkeletonCard>
        </div>
        <div className="px-1 pt-2">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">{title}</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
    </PageFrame>
  );
}

function DashboardPageSkeleton() {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-36" subtitleWidth="w-60" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} className="space-y-3 px-4 py-4 sm:px-5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-32" />
            </SkeletonCard>
          ))}
        </div>
        <div className="grid gap-3 lg:grid-cols-[1.4fr_0.9fr]">
          <SkeletonCard className="space-y-3 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-40 w-full rounded-[1.1rem]" />
          </SkeletonCard>
          <SkeletonCard className="space-y-3 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-28" />
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-[1rem]" />
            ))}
          </SkeletonCard>
        </div>
      </div>
    </PageFrame>
  );
}

function ReportPageSkeleton() {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-44" subtitleWidth="w-72" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} className="space-y-3 px-4 py-4 sm:px-5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-32" />
            </SkeletonCard>
          ))}
        </div>
        <SkeletonCard className="space-y-4 px-4 py-5 sm:px-5">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-64 w-full rounded-[1.2rem]" />
        </SkeletonCard>
      </div>
    </PageFrame>
  );
}

function AssistantPageSkeleton() {
  return (
    <PageFrame className="pb-0">
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-40" subtitleWidth="w-64" />
        <SkeletonCard className="overflow-hidden px-0 py-0">
          <div className="space-y-4 px-4 py-4 sm:px-5">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className={cn('flex gap-3', index % 2 === 1 ? 'justify-end' : '')}>
                {index % 2 === 0 ? <SkeletonCircle className="h-10 w-10 shrink-0" /> : null}
                <div className={cn('max-w-[82%] space-y-2', index % 2 === 1 ? 'items-end text-right' : '')}>
                  <Skeleton className={cn('h-3', index % 2 === 1 ? 'w-40 ml-auto' : 'w-28')} />
                  <Skeleton className={cn('h-14 rounded-[1.1rem]', index % 2 === 1 ? 'w-72' : 'w-80')} />
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200/80 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-5">
            <Skeleton className="h-14 w-full rounded-[1.25rem]" />
          </div>
        </SkeletonCard>
      </div>
    </PageFrame>
  );
}

function SettingsPageSkeleton({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <PageFrame>
      <div className="space-y-3">
        <HeaderSkeleton titleWidth="w-44" subtitleWidth="w-64" />
        <div className="grid gap-3 lg:grid-cols-[0.85fr_1.15fr]">
          <SkeletonCard className="space-y-3 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-32" />
            <FilterRowSkeleton chips={3} />
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-[1rem]" />
            ))}
          </SkeletonCard>
          <SkeletonCard className="space-y-4 px-4 py-5 sm:px-5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-72" />
            <Skeleton className="h-12 w-full rounded-2xl" />
            <Skeleton className="h-12 w-full rounded-2xl" />
            <Skeleton className="h-28 w-full rounded-[1rem]" />
          </SkeletonCard>
        </div>
        <div className="px-1 pt-2">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">{title}</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
    </PageFrame>
  );
}

function pickRouteSkeleton(pathname: string) {
  if (pathname.startsWith('/auth')) {
    return <AuthLoadingSkeleton />;
  }

  if (pathname === routePaths.app.dashboard) {
    return <DashboardPageSkeleton />;
  }

  if (pathname === routePaths.app.gettingStarted) {
    return <SettingsPageSkeleton title="Getting started" subtitle="Checking setup status and clinic onboarding." />;
  }

  if (pathname === routePaths.app.assistant) {
    return <AssistantPageSkeleton />;
  }

  if (pathname === routePaths.app.reports) {
    return <ReportPageSkeleton />;
  }

  if (pathname === routePaths.app.profile) {
    return <FormPageSkeleton title="Profile" subtitle="Loading account and doctor details." />;
  }

  if (pathname === routePaths.app.patients) {
    return <ListPageSkeleton title="Patients" subtitle="Loading the patient list and filters." />;
  }

  if (pathname === routePaths.app.patientQuickAdd || pathname === routePaths.app.patientNew || /\/app\/patients\/[^/]+\/edit$/.test(pathname)) {
    return <FormPageSkeleton title="Patient form" subtitle="Loading patient registration fields." />;
  }

  if (/\/app\/patients\/[^/]+\/history$/.test(pathname) || (/\/app\/patients\/[^/]+$/.test(pathname) && pathname !== routePaths.app.patientQuickAdd && pathname !== routePaths.app.patientNew)) {
    return <DetailPageSkeleton title="Patient profile" subtitle="Loading patient details, history, and activity." />;
  }

  if (pathname === routePaths.app.schedule) {
    return <ListPageSkeleton title="Schedule" subtitle="Loading the appointment timeline and visit queue." />;
  }

  if (pathname === routePaths.app.scheduleNew) {
    return <FormPageSkeleton title="New appointment" subtitle="Loading appointment creation fields." />;
  }

  if (pathname === routePaths.app.invoices) {
    return <ListPageSkeleton title="Bills" subtitle="Loading invoices and payment history." />;
  }

  if (pathname === routePaths.app.invoiceNew || /\/app\/invoices\/[^/]+\/edit$/.test(pathname)) {
    return <FormPageSkeleton title="Bill form" subtitle="Loading invoice details and billing inputs." />;
  }

  if (
    pathname === routePaths.app.masters ||
    pathname === routePaths.app.mastersMenu ||
    pathname === routePaths.app.mastersDoctors ||
    pathname === routePaths.app.mastersProcedures ||
    pathname === routePaths.app.mastersAppointmentTypes
  ) {
    return <SettingsPageSkeleton title="Setup" subtitle="Loading practice settings and master data." />;
  }

  if (
    pathname === routePaths.app.adminMenu ||
    pathname === routePaths.app.adminClinicProfile ||
    pathname === routePaths.app.adminImport ||
    pathname === routePaths.app.adminRoles ||
    pathname === routePaths.app.adminRoleNew ||
    /\/app\/admin\/roles\/[^/]+$/.test(pathname) ||
    pathname === routePaths.app.adminNotificationDeliveries ||
    pathname === routePaths.app.adminUsers ||
    pathname === routePaths.app.owner ||
    pathname === routePaths.app.errors ||
    pathname === routePaths.app.systemHealth
  ) {
    return <SettingsPageSkeleton title="Admin" subtitle="Loading administration, logs, and controls." />;
  }

  return <ListPageSkeleton title="Loading" subtitle="Preparing the page layout." />;
}

export function RouteLoadingFallback() {
  const location = useLocation();

  return pickRouteSkeleton(location.pathname);
}
